import React, { useState, useEffect } from 'react'
import { Box, Stack, Heading, Text, Button, Alert, Loading } from '@/components/lib'
import { useSearchParams, useNavigate } from 'react-router'
import api from '../api'
import useStore from '../store'

// The consent screen for our OAuth authorization server. External applications
// (Claude, ChatGPT, Copilot) send the browser to the API's /oauth/authorize,
// which validates the request and redirects here. Consent lives in the SPA
// rather than the API because the API authenticates with bearer tokens, not
// cookies — it cannot identify the user from a plain browser navigation, but we
// hold the login token and can.
export default function OAuthConsent () {
  const [searchParams] = useSearchParams()
  const [details, setDetails] = useState(null)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const { user, loadingUser } = useStore()
  const navigate = useNavigate()

  const params = Object.fromEntries(searchParams.entries())

  useEffect(() => {
    if (loadingUser) return
    if (!user) {
      // An already-signed-in user must not be sent back to the login screen.
      // On a cold page load the store starts empty and App's auto-login runs in
      // its own effect — and React runs child effects before parent ones, so
      // this effect always sees user: null and loadingUser: false first. Check
      // the stored token instead: if one is present, auto-login is about to
      // populate the store and we just wait for the re-render. A rejected token
      // is cleared by the 401 handler, which clears the store too, so this
      // cannot wait forever.
      if (window.localStorage.getItem('auth_token')) return
      // Genuinely signed out. Come back here afterwards rather than dropping
      // them on the dashboard having lost the authorization request.
      window.sessionStorage.setItem('postLoginRedirect', window.location.pathname + window.location.search)
      navigate('/login')
      return
    }
    api.oauth.getAuthorizationRequest(params, setDetails, err => setError(err.message))
  }, [user, loadingUser])

  const allow = () => {
    setSubmitting(true)
    api.oauth.approve(params, ({ redirectTo }) => {
      // Leaves the SPA entirely — hand the browser back to the application.
      window.location.href = redirectTo
    }, err => {
      setError(err.message)
      setSubmitting(false)
    })
  }

  const deny = () => navigate('/')

  if (loadingUser || (!details && !error)) return <Loading />

  return (
    <Box maxW='lg' mx='auto' mt={10} p={5} rounded='lg' borderWidth='1px'>
      {error
        ? (
          <Stack gap={4}>
            <Heading as='h2' size='md'>This request can't be completed</Heading>
            <Alert status='error'>{error}</Alert>
            <Text>
              Nothing has been shared. Close this page and try connecting again from the
              application you started in.
            </Text>
          </Stack>)
        : (
          <Stack gap={4}>
            <Heading as='h2' size='md'>
              Connect {details.clientName} to Trialflare?
            </Heading>
            <Text>
              <strong>{details.clientName}</strong> is asking to access your Trialflare
              account as <strong>{details.userEmail}</strong>.
            </Text>

            <Box>
              <Text fontWeight='bold' mb={1}>It will be able to:</Text>
              <Stack as='ul' gap={1} pl={5}>
                {details.scopes.map(scope => (
                  <Text as='li' key={scope.id}>{scope.description}</Text>
                ))}
              </Stack>
            </Box>

            {details.grantsWrite
              ? (
                <Alert status='warning'>
                  This application will be able to change trial data according to the permissions on your account.
                </Alert>)
              : (
                <Alert status='info'>
                  It can only see what your own Trialflare permissions allow, and it cannot
                  change anything.
                </Alert>)}

            <Alert status={details.isLoopback ? 'warning' : 'info'}>
              {details.isLoopback
                ? `You will be returned to an application running on this computer (${details.redirectHost}). Only continue if you started this yourself.`
                : `You will be returned to ${details.redirectHost}.`}
            </Alert>

            <Stack direction='row' gap={3}>
              <Button colorPalette='primary' onClick={allow} loading={submitting}>
                Allow
              </Button>
              <Button variant='outline' onClick={deny} disabled={submitting}>
                Cancel
              </Button>
            </Stack>
          </Stack>)}
    </Box>
  )
}
